import i18n from "@/shared/config/i18n";
import AuthService from "@/shared/auth/AuthService";
import { authPort } from "@/shared/auth/authPort";
import { showAlert } from "@/shared/store/alertStore";
import { getDeviceId } from "@/shared/lib/deviceId";
import { tokenStorage } from "@/shared/auth/tokenStorage";
import { useConfirm } from "@/shared/ui/alert-dialog/useConfirm";

// 409 = сессия истекла на стороне бэка. Показываем неотменяемую плашку через
// тот же механизм, что и глобальный $confirm, и уводим на авторизацию.
const confirm = useConfirm();
let sessionExpiredShown = false;

function redirectToAuth() {
  tokenStorage.clear();
  window.location.reload();
}

function handleSessionExpired() {
  // Параллельные запросы могут вернуть 409 пачкой — показываем плашку один раз.
  if (sessionExpiredShown) return;
  sessionExpiredShown = true;

  confirm(
    i18n.global.t("Ваша сессия истекла. Войдите снова, чтобы продолжить."),
    i18n.global.t("Сессия истекла"),
    { confirmButtonText: i18n.global.t("Войти снова") }
  )
    // resolve — нажали кнопку, reject — закрыли по Escape: в обоих случаях
    // сессии уже нет, поэтому одинаково уводим на авторизацию.
    .then(redirectToAuth, redirectToAuth);
}

// AuthService — instance-метод refreshTokens, поэтому держим ленивый singleton.
// Раньше код вызывал `AuthService.refreshTokens(...)` как static — это тихо
// бросало TypeError и приводило к бесконечному reload через catch.
let _authService = null;
function getAuthService() {
  if (!_authService) {
    const storagePort = {
      save(k, v) {
        localStorage.setItem(k, v);
      },
      load(k) {
        return localStorage.getItem(k);
      },
    };
    _authService = new AuthService(authPort, storagePort);
  }
  return _authService;
}

export default function interceptors(client) {
  client.interceptors.request.use(
    (config) => {
      const token = tokenStorage.accessToken;
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      const deviceId = getDeviceId();
      if (deviceId) {
        config.headers["X-Device-ID"] = deviceId;
      }

      return config;
    },
    (error) => Promise.reject(error)
  );

  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      // Guard: 401 от самого /login/refresh/ НЕ должен триггерить refresh-
      // рекурсию — иначе бесконечная цепочка HTTP-запросов до rate-limit.
      const isRefreshCall = originalRequest?.url?.includes("/login/refresh/");

      if (
        error.response?.status === 401 &&
        !originalRequest._retry &&
        !isRefreshCall
      ) {
        originalRequest._retry = true;

        try {
          const result = await getAuthService().refreshTokens("nclayer");

          if (result?._success) {
            originalRequest.headers.Authorization = `Bearer ${result.access_token}`;
            return client(originalRequest);
          }
        } catch (refreshError) {
          console.error(
            "Ошибка при обновлении токенов через интерсептор:",
            refreshError
          );

          tokenStorage.clear();
          window.location.reload();
        }
      }

      return Promise.reject(error);
    }
  );

  const SUCCESS_METHODS = ["post", "put", "patch"];

  client.interceptors.response.use(
    (response) => {
      const method = response.config.method?.toLowerCase();
      const meta = response.config.metaData || {};

      if (meta?.isNotificationDisabled) {
        return response;
      }

      if (
        response.status === 200 &&
        SUCCESS_METHODS.includes(method) &&
        response.data?.success
      ) {
        showAlert(
          response.data?.message || i18n.global.t("Успешно"),
          "",
          "success"
        );
      }

      return response;
    },

    (error) => {
      // Сессия истекла — показываем плашку вместо обычного error-тоста.
      if (error?.response?.status === 409) {
        handleSessionExpired();
        return Promise.reject(error);
      }

      const meta = error?.config?.metaData || {};

      if (meta?.isNotificationDisabled) {
        return Promise.reject(error);
      }

      let detail_message = error?.response?.data?.payload?.detail;
      const message = "Ошибка";
      const description =
        detail_message || error?.request?.responseText || error?.message;

      showAlert(message, description, "error");

      return Promise.reject(error);
    }
  );
}
